import React, { useContext, useMemo } from 'react';
import startCase from 'lodash/startCase';

import Row from 'components/Common/Row';
import Typography from 'kingpin/atoms/Typography';
import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';
import useValueFormatters from 'hooks/useValueFormatters';

import useBonusSubPeriodRows from '../../hooks/useBonusSubPeriodRows';
import resolveHeaderTotalField from '../headerTotalField';

/**
 * Section header for a Bonus Reward section on a sub-period board: the reward
 * name and its total across the parent period's weeks. Occupies the top row (in
 * place of the single-week reward heading) so the fact table beneath carries no
 * duplicate title. Shares the weekly rows with the fact table via
 * useBonusSubPeriodRows.
 */
const BonusSubPeriodRewardHeading = ({
  bonusConfigId,
  rewardName,
  totalValueField,
}: {
  bonusConfigId: string;
  rewardName: string;
  // Overrides which dataset field the header total sums/formats (e.g. show
  // `safetyBonusPay` $ instead of the reward's own `safetyPoints`).
  totalValueField?: string;
}) => {
  const { bonusPrograms } = useContext(DatasetDefinitionsContext);
  const { formatField } = useValueFormatters();
  const { rows, dataType } = useBonusSubPeriodRows(bonusConfigId);

  const rewardValueField = useMemo(() => {
    const program = bonusPrograms.find((p) => p.id === bonusConfigId);
    return program?.rewardFields.find((f) => f.name === rewardName)
      ?.rewardValueField;
  }, [bonusPrograms, bonusConfigId, rewardName]);

  const totalField = resolveHeaderTotalField({
    totalValueField,
    fallbackField: rewardValueField,
  });

  const total = useMemo(() => {
    if (!totalField) {
      return undefined;
    }
    return rows.reduce((sum, row) => {
      const value = row.doc?.[totalField];
      return typeof value === 'number' ? sum + value : sum;
    }, 0);
  }, [rows, totalField]);

  return (
    <Row spaceBetween centerAlign style={{ width: '100%' }}>
      <Typography.Header type="H5">{startCase(rewardName)}</Typography.Header>
      {dataType && totalField && total !== undefined && (
        <Typography.Header type="H5">
          {formatField({
            value: total,
            field: totalField,
            dataset: dataType,
          })}
        </Typography.Header>
      )}
    </Row>
  );
};

export default BonusSubPeriodRewardHeading;
